import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { GetBillTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillTosDataView } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import translate from "@insite/client-framework/Translate";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    noShipToAndCantCreate: boolean;
    setParameter: (parameter: GetBillTosApiParameter) => void;
    parameter: GetBillTosApiParameter;
    billTo?: BillToModel;
    onSelect: (billTo: BillToModel) => void;
    extendedStyles?: ChangeCustomerBillToSelectorStyles;
    isLoading?: boolean;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    billTosDataView: getBillTosDataView(state, props.parameter),
    customerSettings: getSettingsCollection(state).customerSettings,
});

const mapDispatchToProps = {
    loadBillTos,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ChangeCustomerBillToSelectorStyles {
    billToSelectorDynamicDropdown?: DynamicDropdownPresentationProps;
}

export const changeCustomerBillToSelectorStyles: ChangeCustomerBillToSelectorStyles = {};

const ChangeCustomerBillToSelector: FC<Props> = ({
    noShipToAndCantCreate,
    parameter,
    setParameter,
    billTo,
    onSelect,
    extendedStyles,
    isLoading,
    billTosDataView,
}) => {
    const [styles] = useState(() => mergeToNew(changeCustomerBillToSelectorStyles, extendedStyles));
    const [options, setOptions] = useState<OptionObject[]>([]);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(() => {
        return () => {
            if (debounceTimer.current !== undefined) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);

    useEffect(() => {
        if (billTosDataView.value) {
            const options: OptionObject[] = billTosDataView.value.map(billTo => ({
                optionText: billTo.label,
                optionValue: billTo.id,
            }));
            setOptions(options);
        }
    }, [billTosDataView]);

    const searchTextChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        if (debounceTimer.current !== undefined) {
            clearTimeout(debounceTimer.current);
        }
        if (value.length === 0 || value.length >= 4) {
            debounceTimer.current = setTimeout(() => {
                setParameter({
                    ...parameter,
                    filter: value,
                });
            }, 300);
        }
    };

    const selectCustomerHandler = (billToId?: string) => {
        if (billTosDataView.value && billToId) {
            const billTo = billTosDataView.value.filter(billTo => billTo.id === billToId)[0];
            if (billTo) {
                onSelect(billTo as BillToModel);
            }
        }
    };

    return (
        <DynamicDropdown
            label={translate("Select Customer")}
            {...styles.billToSelectorDynamicDropdown}
            onSelectionChange={selectCustomerHandler}
            onInputChange={searchTextChanged}
            selected={billTo?.id}
            options={options}
            placeholder={translate("Search by Cust. No, Dealer Code, Cust. Name, City, State")}
            isLoading={isLoading}
            error={noShipToAndCantCreate && siteMessage("SignIn_NoShipToAndCantCreate")}
            data-test-selector="changeCustomerBillToSelector"
            filterOption={() => true}
        />
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangeCustomerBillToSelector);
